import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Shield, User, HelpCircle, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card.js";
import { api } from "../lib/api.js";
import { getIdToken, parseIdToken } from "../lib/auth.js";

type Role = "admin" | "user" | "unconfirmed";

const ROLE_META: Record<Role, { label: string; color: string; icon: typeof Shield }> = {
  admin: { label: "Admin", color: "bg-blue-100 text-blue-700", icon: Shield },
  user: { label: "User", color: "bg-emerald-100 text-emerald-700", icon: User },
  unconfirmed: {
    label: "Unconfirmed",
    color: "bg-amber-100 text-amber-700",
    icon: HelpCircle,
  },
};

function deriveRole(groups: string[]): Role | null {
  if (groups.includes("admin")) return "admin";
  if (groups.includes("user")) return "user";
  if (groups.includes("unconfirmed")) return "unconfirmed";
  return null;
}

export function UserManagementModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Identify "me" so we can block self-demotion and self-delete in the UI.
  // Server also enforces this; the UI guard is just UX.
  const token = getIdToken();
  const me = token ? parseIdToken(token) : null;
  const mySub = me?.sub ?? null;
  const myUsername = me?.username ?? null;

  const usersQ = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.adminListUsers(),
  });

  const setRoleMut = useMutation({
    mutationFn: ({ username, role }: { username: string; role: Role }) =>
      api.adminSetUserRole(username, role),
    onSuccess: () => {
      setErrorMsg(null);
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err) =>
      setErrorMsg(err instanceof Error ? err.message : "Role change failed"),
  });

  const deleteUserMut = useMutation({
    mutationFn: (username: string) => api.adminDeleteUser(username),
    onSuccess: () => {
      setConfirmDelete(null);
      setErrorMsg(null);
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err) =>
      setErrorMsg(err instanceof Error ? err.message : "Delete failed"),
  });

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <Card
        className="flex max-h-[80vh] w-full max-w-2xl flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold">User Management</CardTitle>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          {errorMsg && (
            <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMsg}
            </div>
          )}
          {usersQ.isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          )}
          {usersQ.data && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="pb-2 font-medium text-slate-500">Email</th>
                  <th className="pb-2 font-medium text-slate-500">Role</th>
                  <th className="pb-2 font-medium text-slate-500">Status</th>
                  <th className="pb-2 text-right font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersQ.data.users.map((u) => {
                  const role = deriveRole(u.groups);
                  const isSelf = !!(
                    (mySub && u.sub === mySub) ||
                    (myUsername && u.username === myUsername)
                  );
                  const busy =
                    (setRoleMut.isPending &&
                      setRoleMut.variables?.username === u.username) ||
                    (deleteUserMut.isPending &&
                      deleteUserMut.variables === u.username);
                  const badge = role
                    ? ROLE_META[role]
                    : { label: "No group", color: "bg-slate-100 text-slate-500", icon: HelpCircle };
                  const BadgeIcon = badge.icon;
                  return (
                    <tr
                      key={u.username}
                      className="border-b border-slate-100 last:border-0"
                    >
                      <td className="py-3 text-slate-900">
                        {u.email ?? u.username}
                        {isSelf && (
                          <span className="ml-1.5 text-xs text-slate-400">
                            (you)
                          </span>
                        )}
                      </td>
                      <td className="py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${badge.color}`}
                        >
                          <BadgeIcon className="h-3 w-3" />
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-3 text-slate-500">{u.status}</td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-2">
                          <select
                            value={role ?? ""}
                            disabled={busy || isSelf}
                            title={
                              isSelf
                                ? "You cannot change your own role"
                                : "Change role"
                            }
                            onChange={(e) => {
                              const next = e.target.value as Role;
                              if (next === role) return;
                              setRoleMut.mutate({
                                username: u.username,
                                role: next,
                              });
                            }}
                            className="rounded border border-slate-200 bg-white px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {role === null && (
                              <option value="" disabled>
                                (no group)
                              </option>
                            )}
                            <option value="admin">Admin</option>
                            <option value="user">User</option>
                            <option value="unconfirmed">Unconfirmed</option>
                          </select>

                          {confirmDelete === u.username ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => deleteUserMut.mutate(u.username)}
                                disabled={deleteUserMut.isPending}
                                className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setConfirmDelete(null)}
                                className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-50"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDelete(u.username)}
                              disabled={isSelf}
                              title={
                                isSelf
                                  ? "You cannot delete your own account"
                                  : "Delete user"
                              }
                              className="rounded p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-red-400"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
